import React, { useState, useMemo } from 'react';
import { Info } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Label } from '../ui/label';
import { Input } from '../ui/input';

const RETIREMENT_SYSTEMS = [
  { value: 'high3', label: 'High-3', desc: 'Entered before Jan 1, 2018' },
  { value: 'brs', label: 'BRS (Blended)', desc: 'Entered Jan 1, 2018+' },
  { value: 'final', label: 'Final Pay', desc: 'Entered before Sept 8, 1980' },
];

// Simplified 2026 base pay table (monthly) for common ranks/YOS
const BASE_PAY_2026 = {
  'E-5': { 6: 3455, 8: 3619, 10: 3802, 12: 3987, 14: 4099, 16: 4223, 18: 4345, 20: 4465 },
  'E-6': { 6: 3819, 8: 4034, 10: 4228, 12: 4422, 14: 4617, 16: 4815, 18: 5015, 20: 5220 },
  'E-7': { 6: 4284, 8: 4503, 10: 4700, 12: 4899, 14: 5100, 16: 5304, 18: 5510, 20: 5720, 22: 5935, 24: 6120, 26: 6340 },
  'E-8': { 8: 5204, 10: 5420, 12: 5640, 14: 5865, 16: 6095, 18: 6330, 20: 6570, 22: 6815, 24: 7060, 26: 7290 },
  'E-9': { 10: 6367, 12: 6590, 14: 6820, 16: 7055, 18: 7295, 20: 7540, 22: 7790, 24: 8045, 26: 8310, 28: 8580, 30: 8850 },
  'O-3': { 4: 6262, 6: 6762, 8: 7095, 10: 7352, 12: 7720, 14: 8035, 16: 8200, 18: 8200, 20: 8200 },
  'O-4': { 6: 7522, 8: 7920, 10: 8382, 12: 8765, 14: 9135, 16: 9420, 18: 9620, 20: 9820 },
  'O-5': { 8: 8880, 10: 9285, 12: 9812, 14: 10210, 16: 10610, 18: 10960, 20: 11320, 22: 11695, 24: 11695, 26: 11695 },
  'O-6': { 10: 10540, 12: 10920, 14: 11490, 16: 11990, 18: 12540, 20: 13150, 22: 13530, 24: 13910, 26: 14290, 28: 14980, 30: 15310 },
};

const RANKS = Object.keys(BASE_PAY_2026);

function getBasePay(rank, yos) {
  const table = BASE_PAY_2026[rank];
  if (!table) return 0;
  const years = Object.keys(table).map(Number).sort((a, b) => a - b);
  let pay = 0;
  for (const y of years) {
    if (yos >= y) pay = table[y];
  }
  return pay;
}

function getHigh3Average(rank, yos) {
  // Average of last 36 months (approximate by averaging YOS, YOS-1, YOS-2)
  const p1 = getBasePay(rank, yos);
  const p2 = getBasePay(rank, Math.max(0, yos - 1));
  const p3 = getBasePay(rank, Math.max(0, yos - 2));
  return (p1 + p2 + p3) / 3;
}

const RetirementPayCalculator = () => {
  const [system, setSystem] = useState('high3');
  const [rank, setRank] = useState('E-7');
  const [yearsOfService, setYearsOfService] = useState(20);
  const [tspBalance, setTspBalance] = useState(250000);
  const [sbpElected, setSbpElected] = useState(false);

  const results = useMemo(() => {
    const yos = Math.max(1, Math.min(40, yearsOfService));
    const high3Avg = getHigh3Average(rank, yos);
    const finalPay = getBasePay(rank, yos);

    let multiplier, monthlyBase, monthlyRetirement;

    switch (system) {
      case 'high3':
        multiplier = 2.5;
        monthlyBase = high3Avg;
        monthlyRetirement = monthlyBase * (multiplier * yos / 100);
        break;
      case 'brs':
        multiplier = 2.0;
        monthlyBase = high3Avg;
        monthlyRetirement = monthlyBase * (multiplier * yos / 100);
        break;
      case 'final':
        multiplier = 2.5;
        monthlyBase = finalPay;
        monthlyRetirement = monthlyBase * (multiplier * yos / 100);
        break;
      default:
        multiplier = 2.5;
        monthlyBase = high3Avg;
        monthlyRetirement = monthlyBase * (multiplier * yos / 100);
    }

    const sbpCost = sbpElected ? monthlyRetirement * 0.065 : 0;
    const afterSbp = monthlyRetirement - sbpCost;

    // BRS continuation pay (typically 2.5-13x monthly base at 12 YOS)
    const brsContinuationPay = system === 'brs' ? monthlyBase * 6 : 0;

    // TSP matching for BRS
    const tspMonthlyWithdrawal = tspBalance > 0 ? tspBalance / (12 * 25) : 0; // 25-year drawdown

    return {
      monthlyBase,
      multiplier,
      percentOfPay: multiplier * yos,
      monthlyRetirement: Math.round(monthlyRetirement * 100) / 100,
      annualRetirement: Math.round(monthlyRetirement * 12 * 100) / 100,
      sbpCost: Math.round(sbpCost * 100) / 100,
      afterSbp: Math.round(afterSbp * 100) / 100,
      brsContinuationPay: Math.round(brsContinuationPay),
      tspMonthlyWithdrawal: Math.round(tspMonthlyWithdrawal * 100) / 100,
      totalMonthlyIncome: Math.round((afterSbp + tspMonthlyWithdrawal) * 100) / 100,
    };
  }, [system, rank, yearsOfService, tspBalance, sbpElected]);

  return (
    <div className="space-y-6" data-testid="retirement-calculator">
      {/* System Selection */}
      <div>
        <Label className="text-sm font-semibold text-slate-800 mb-2 block">Retirement System</Label>
        <div className="grid grid-cols-3 gap-2">
          {RETIREMENT_SYSTEMS.map(s => (
            <button
              key={s.value}
              onClick={() => setSystem(s.value)}
              data-testid={`system-${s.value}`}
              className={`p-3 rounded-lg border text-left transition-all ${
                system === s.value
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-slate-700 border-slate-200 hover:border-blue-300'
              }`}
            >
              <p className="text-sm font-semibold">{s.label}</p>
              <p className={`text-[10px] ${system === s.value ? 'text-blue-100' : 'text-slate-500'}`}>{s.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Inputs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-sm font-medium text-slate-700">Rank at Retirement</Label>
          <select
            value={rank}
            onChange={(e) => setRank(e.target.value)}
            className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 text-sm bg-white"
            data-testid="retirement-rank"
          >
            {RANKS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-sm font-medium text-slate-700">Years of Service</Label>
          <Input
            type="number"
            min={1}
            max={40}
            value={yearsOfService}
            onChange={(e) => setYearsOfService(Number(e.target.value))}
            className="mt-1"
            data-testid="retirement-yos"
          />
        </div>
        {system === 'brs' && (
          <div>
            <Label className="text-sm font-medium text-slate-700">TSP Balance at Retirement</Label>
            <Input
              type="number"
              min={0}
              value={tspBalance}
              onChange={(e) => setTspBalance(Number(e.target.value))}
              className="mt-1"
              data-testid="tsp-balance"
            />
          </div>
        )}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="sbp"
            checked={sbpElected}
            onChange={(e) => setSbpElected(e.target.checked)}
            className="rounded"
          />
          <Label htmlFor="sbp" className="text-sm text-slate-700 cursor-pointer">Elect SBP (6.5% deduction)</Label>
        </div>
      </div>

      {/* Results */}
      <Card className="border-green-200 bg-gradient-to-br from-green-50 to-white">
        <CardContent className="p-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-white rounded-lg border">
              <p className="text-[10px] text-slate-500 mb-0.5">Multiplier</p>
              <p className="text-xl font-bold text-slate-800">{results.percentOfPay}%</p>
              <p className="text-[10px] text-slate-400">{results.multiplier}% x {yearsOfService} yrs</p>
            </div>
            <div className="text-center p-3 bg-white rounded-lg border">
              <p className="text-[10px] text-slate-500 mb-0.5">Monthly Retirement</p>
              <p className="text-xl font-bold text-green-700" data-testid="monthly-retirement">${results.monthlyRetirement.toLocaleString()}</p>
              {sbpElected && <p className="text-[10px] text-slate-400">Before SBP</p>}
            </div>
            <div className="text-center p-3 bg-white rounded-lg border">
              <p className="text-[10px] text-slate-500 mb-0.5">After SBP</p>
              <p className="text-xl font-bold text-green-700">${results.afterSbp.toLocaleString()}</p>
              {sbpElected && <p className="text-[10px] text-red-500">-${results.sbpCost.toLocaleString()}/mo</p>}
            </div>
            <div className="text-center p-3 bg-white rounded-lg border">
              <p className="text-[10px] text-slate-500 mb-0.5">Annual</p>
              <p className="text-xl font-bold text-green-700">${results.annualRetirement.toLocaleString()}</p>
            </div>
          </div>

          {system === 'brs' && (
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-[10px] text-blue-600 font-medium">TSP Monthly (25yr drawdown)</p>
                <p className="text-lg font-bold text-blue-700">${results.tspMonthlyWithdrawal.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                <p className="text-[10px] text-emerald-600 font-medium">Total Monthly Income</p>
                <p className="text-lg font-bold text-emerald-700">${results.totalMonthlyIncome.toLocaleString()}</p>
              </div>
            </div>
          )}

          {system === 'brs' && (
            <div className="mt-3 flex items-start gap-2 text-xs text-blue-700 bg-blue-100/50 p-2.5 rounded-lg">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>BRS includes 5% TSP matching. Continuation pay (~{results.brsContinuationPay > 0 ? `$${results.brsContinuationPay.toLocaleString()}` : 'varies'}) is a one-time lump sum at 12 YOS.</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RetirementPayCalculator;
